import { CloseOutlined } from "@ant-design/icons";
import { Button, Flex } from "antd";
import { t } from "i18next";
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import {
  FilterItem,
  FilterItemValue,
} from "../../modules/filters/filters.types";
import { checkIfSomeObjectFieldsAreFilled } from "../../utils/functions";
import { FilterItem as FilterItemComponent } from "./components/FilterItem";
import style from "./FilterToolbar.module.scss";
interface FilterToolbarProps<T extends object> extends PropsWithChildren {
  filters: T;
  filterItems?: Array<FilterItem<T>>;
  onFilterChange: (filters: { [key: string]: FilterItemValue }) => void;
  clearFilters?: () => void;
  className?: string;
  showApplyButton?: boolean;
  applyDisabledIfEmpty?: Array<keyof T>;
  setManualFilters?: (filters: { [key: string]: FilterItemValue }) => void;
}

const isFilterValueEmpty = (value: FilterItemValue) =>
  value === null || value === undefined || value === "";

const FilterToolbar = <T extends object>({
  children,
  filters,
  filterItems,
  className,
  onFilterChange,
  clearFilters,
  setManualFilters,
  showApplyButton,
  applyDisabledIfEmpty,
}: FilterToolbarProps<T>) => {
  const clearFiltersButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const slideOut = () => {
    if (clearFiltersButtonRef.current) {
      clearFiltersButtonRef.current.classList.remove(style.slideIn);
      clearFiltersButtonRef.current.classList.add(style.slideOut);
    }
    setTimeout(() => setIsVisible(false), 400);
  };

  const onApplyButtonClick = () => {
    setManualFilters?.(filters as { [key: string]: FilterItemValue });
  };

  const isApplyDisabled = useMemo(() => {
    if (!applyDisabledIfEmpty?.length || !filters) return false;

    return applyDisabledIfEmpty.some((fieldName) =>
      isFilterValueEmpty(filters[fieldName] as FilterItemValue)
    );
  }, [applyDisabledIfEmpty, filters]);

  const showClearFiltersButton = useMemo(() => {
    if (clearFilters && filters) {
      return checkIfSomeObjectFieldsAreFilled(filters);
    }
    return false;
  }, [clearFilters, filters]);

  useEffect(() => {
    if (!showClearFiltersButton) {
      slideOut();
    } else {
      setTimeout(() => setIsVisible(true), 400);
    }
  }, [showClearFiltersButton]);

  return (
    <Flex align="center" wrap="wrap" gap={8} className={className}>
      {filterItems?.map((filter, index) => (
        <Flex key={index} flex={1}>
          <FilterItemComponent
            {...filter}
            value={
              filter.formatForClient
                ? filter.formatForClient(
                    filters?.[filter.fieldName] as FilterItemValue
                  )
                : (filters?.[filter.fieldName] as FilterItemValue)
            }
            onChange={(value) =>
              onFilterChange({
                [filter.fieldName]: filter.formatForApi
                  ? filter.formatForApi(value)
                  : value,
              })
            }
          />
        </Flex>
      ))}
      {showApplyButton && (
        <Button
          type="primary"
          disabled={isApplyDisabled}
          onClick={onApplyButtonClick}
        >
          {t("COMMON.PRETRAZI")}
        </Button>
      )}
      {children}
      {clearFilters && isVisible ? (
        <Button
          ref={clearFiltersButtonRef}
          type="link"
          onClick={clearFilters}
          icon={<CloseOutlined />}
          className={`${style.clearFiltersButton} ${style.slideIn}`}
        >
          {t("COMMON.OCISTI_FILTERE")}
        </Button>
      ) : (
        <></>
      )}
    </Flex>
  );
};

export default FilterToolbar;
